import React, { SyntheticEvent, useState } from 'react';
import { IconSize, Spacing } from 'modules/styles/variables';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import { Alert } from 'components/Alert';
import { MuiBox } from 'theme/material-ui';
import DefaultText from 'components/UI/Typography/DefaultText';
import { getFileSizeInMegaBytes } from 'utils';
import { Dialog } from '@material-ui/core';
import CloseButton from 'components/UI/Button/CloseButton';
import { Img, AddFileButton, FloatAccept, RotateImage } from 'screens/PreRegister/styled';
import StyledButton from 'components/UI/Button/StyledButton';
import { Icon } from 'components/Icon';

// TODO add tests for this. We have a deadline. I will add tests at the end of the day.
function rotateImage(base64Image: string): string {
  // create an off-screen canvas
  const offScreenCanvas = document.createElement('canvas');
  const offScreenCanvasCtx: CanvasRenderingContext2D = offScreenCanvas.getContext(
    '2d'
  ) as CanvasRenderingContext2D;

  // create Image
  const img = new window.Image();
  img.src = base64Image;

  // set its dimension to rotated size
  offScreenCanvas.height = img.width;
  offScreenCanvas.width = img.height;

  // rotate and draw source image into the off-screen canvas:
  offScreenCanvasCtx.rotate((90 * Math.PI) / 180);
  offScreenCanvasCtx.translate(0, -offScreenCanvas.width);

  offScreenCanvasCtx.drawImage(img, 0, 0);

  // encode image to data-uri with base64
  return offScreenCanvas.toDataURL('image/jpeg', 100);
}

export function resizeImage(imageLoaded: HTMLImageElement, maxDimensions: number): string {
  // Resize the image
  const { width, height } = imageLoaded;
  const ratio = width / height;

  let newWidth = maxDimensions;
  let newHeight = maxDimensions;

  if (width > height) {
    newHeight = Math.round(newWidth / ratio);
  } else {
    newWidth = Math.round(newHeight * ratio);
  }

  // create an off-screen canvas
  const offScreenCanvas = document.createElement('canvas');
  offScreenCanvas.width = newWidth;
  offScreenCanvas.height = newHeight;

  // Create context
  const offScreenCanvasCtx: CanvasRenderingContext2D = offScreenCanvas.getContext(
    '2d'
  ) as CanvasRenderingContext2D;
  offScreenCanvasCtx.drawImage(imageLoaded, 0, 0, newWidth, newHeight);

  return offScreenCanvas.toDataURL('image/jpeg');
}

interface CameraButtonProps {
  id: string;
  onSuccess: (base64File: string, fileName: string) => void;
  text: string;
  accept?: string;
  margin?: string;
  onError?: (event: Error) => void;
  maxFileSize?: number; // In Megabytes
  maxDimensions?: number; // Max width or height in pixels;
}
/**
 * Method: CameraButton
 * Description: Button that has a camera icon that will allow a file upload.
 * default accept     
    HEIC
    JPEG
    BMP
    PNG
    JPG
 */
export default function CameraButton({
  accept = 'image/*, .heic',
  id,
  margin,
  onError,
  onSuccess,
  text,
  maxFileSize,
  maxDimensions
}: CameraButtonProps) {
  const [verifyImage, setVerifyImage] = useState(false);
  const [tempData, setTempData] = useState<{ fileString: string; fileName: string } | undefined>();
  if (!id) {
    // Throwing here because it is possible to have two of these buttons on a page. The file id needs to be unique to grab the correct one later.
    throw new Error('CameraButton requires a unique id');
  }
  const defaultMargin = `${Spacing.large}px`;

  const handleFileUploaded = (evt: SyntheticEvent) => {
    const file = evt.target.files[0]; // Grab the file
    const reader = new window.FileReader();

    // Handle the loaded event. reader.result is a base64 encoded string of the file.
    reader.onloadend = () => {
      const fileString = reader.result ? (reader.result as string) : '';
      if (maxDimensions) {
        const img = new window.Image();
        img.onload = () => {
          if (img.width > maxDimensions || img.height > maxDimensions) {
            onData(resizeImage(img, maxDimensions), file.name);
          } else {
            onData(fileString, file.name);
          }
        };
        img.src = fileString;
      } else {
        onData(fileString, file.name);
      }
    };

    // Handle any errors uploading
    reader.onerror = () => {
      Alert.alert('There was an error uploading your file. Please try again.');
      if (onError) {
        onError(new Error('There was an error uploading your file. Please try again.'));
      }
    };

    if (file) {
      // Only read it if we got it.
      reader.readAsDataURL(file);
    }
  };

  const onData = (fileString: string, fileName: string) => {
    if (maxFileSize) {
      const fileSize = getFileSizeInMegaBytes(fileString);
      if (fileSize < maxFileSize) {
        setVerifyImage(true);
        setTempData({ fileString, fileName });
      } else {
        const message = `File exceeds max file size of ${maxFileSize}MB`;
        const error = new Error(message);
        if (onError) {
          onError(error);
        } else {
          Alert.alert('Invalid File Size', message);
        }
      }
    } else {
      setVerifyImage(true);
      setTempData({ fileString, fileName });
    }
  };

  const handleAccept = () => {
    setVerifyImage(false);
    if (tempData?.fileString) {
      onSuccess(tempData?.fileString, tempData?.fileName);
    }
    setTempData(undefined);
  };

  // This ties the input to the button, so we can use the style of the button. <input /> does not accept children.
  const handleFileClick = () => {
    const ele = document.getElementById(id) as HTMLElement;
    ele.click();
  };

  const handleRotateImage = () => {
    if (tempData?.fileString) {
      setTempData({ fileString: rotateImage(tempData.fileString), fileName: tempData.fileName });
    }
  };

  return (
    <MuiBox margin={margin || defaultMargin}>
      <Dialog fullScreen open={verifyImage}>
        <CloseButton
          data-testid="camera-close"
          absolute
          onPress={() => {
            setVerifyImage(false);
          }}
          margin="0px"
          padding="3px"
        />
        <Img src={tempData?.fileString} height="100%" />
        <RotateImage onClick={handleRotateImage}>
          <Icon name="cached" size={IconSize.large} />
        </RotateImage>
        <FloatAccept>
          <MuiBox width="inherit" margin={`auto ${Spacing.medium}px`}>
            <StyledButton data-testid="pre-reg-accept-button" title="Accept" onPress={handleAccept}>
              Accept
            </StyledButton>
          </MuiBox>
        </FloatAccept>
      </Dialog>

      <AddFileButton data-testid="camera-button-button" onClick={handleFileClick}>
        <MuiBox textAlign="center">
          <AddAPhotoIcon />
        </MuiBox>
        <DefaultText>{text}</DefaultText>
        <input
          data-testid="camera-button-input"
          id={id}
          type="file"
          onChange={handleFileUploaded}
          accept={accept}
          style={{ display: 'none' }}
        />
      </AddFileButton>
    </MuiBox>
  );
}
